import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

import { env } from '@/env';
import {
  modeOfTimeControl,
  queueBody,
  queueMismatch,
  type QueueEcho,
  type RankedQueueMode,
} from '@/lib/qgammon/queueMode';
import { qgammonAuthHeaders, qgammonSocketAuth } from '@/lib/qgammon/auth';
import { QGAMMON_SOCKET_OPTIONS } from '@/lib/qgammon/socketOptions';
import {
  isMatchmakingCancelReason,
  ServerEvents,
  type MatchmakingCancelledPayload,
  type MatchmakingCancelReason,
  type MatchmakingPairedPayload,
  type MatchmakingQueuedPayload,
} from '@qlashgeminiz/qgammon-types';

export type RankedQueueStatus = 'idle' | 'queueing' | 'queued' | 'paired';

/**
 * Why the search is no longer running. Surfaced by `RankedQueueSheet` so the
 * player is never just dropped back to the lobby with no explanation.
 */
export type RankedQueueNotice =
  /** `POST /matchmaking/queue` → 409: already at the concurrent-match cap. */
  | { kind: 'atMatchCap'; max: number | null }
  /**
   * The SERVER took us out of the queue (`mm:cancelled`). `reason` is only ever
   * one the shared types package knows about; anything else — including reasons
   * added to the protocol after this bundle shipped — degrades to `'unknown'`
   * and gets generic copy. Never switch exhaustively on this: the server's list
   * grows, and the wire schema is deliberately liberal (`z.string()`) for
   * exactly that reason.
   */
  | { kind: 'cancelled'; reason: MatchmakingCancelReason | 'unknown' }
  /** The request itself failed (network / 5xx). */
  | { kind: 'failed' }
  /**
   * 🔴 Il server ha detto `200` e ci ha messi in un bacino DIVERSO da quello
   * chiesto — e senza questa lettura non lo saprebbe nessuno.
   * [M `or43` 09/09, staging]: `POST /matchmaking/queue` **non valida il
   * payload**: un campo che non riconosce lo ignora e ripiega su 180/10
   * rispondendo **200**. ⇒ *Il codice HTTP non e' il discriminante*, e restare
   * in una coda che non si e' chiesta e' indistinguibile — con sei giocatori —
   * da «non c'e' nessuno». Si esce e si dice cosa e' successo.
   *
   * `axis` dice QUALE asse discorda (`queueMismatch`): `clock` = il server ha
   * detto un altro orologio (e `got` lo nomina); `rated` = la coda libera non e'
   * stata confermata FUORI CLASSIFICA (eco senza `rated === false`, o nessuna
   * risposta), o la classificata e' tornata `rated: false`. ⚠️ Su `rated` il
   * nome della coda in `got` NON basta a dirlo: l'orologio puo' concordare e la
   * classifica no — per questo la frase e' un'altra (`RankedQueueSheet`).
   */
  | {
      kind: 'wrongQueue';
      asked: RankedQueueMode;
      got: RankedQueueMode | null;
      axis: 'clock' | 'rated';
    };

export interface RankedQueue {
  status: RankedQueueStatus;
  /** Server-confirmed enqueue time (from `mm:queued`), used for the elapsed clock. */
  enqueuedAt: number | null;
  /** Set once the server pairs us — the caller navigates to the match. */
  paired: MatchmakingPairedPayload | null;
  /** Set when the search stopped for a reason worth telling the player. */
  notice: RankedQueueNotice | null;
  /**
   * QUALE coda si sta cercando, o `null` da fermi.
   *
   * 🔴 Non e' un dettaglio da mostrare: e' l'unica cosa che distingue due
   * attese identiche a schermo. Chi ha premuto «senza orologio» e vede
   * «Cerco un avversario…» non ha modo di sapere in quale dei due bacini sta,
   * e le due code **non si incontrano mai** — un'attesa nel bacino sbagliato
   * e' indistinguibile da un bacino vuoto.
   */
  mode: RankedQueueMode | null;
  /** POST the queue, then wait for `mm:queued`. */
  join: (mode: RankedQueueMode) => void;
  /** DELETE the queue and reset to idle. */
  cancel: () => void;
  /** Acknowledge the notice (closes the sheet). */
  dismissNotice: () => void;
}

/**
 * Drives the qgammon-gameserver ranked matchmaking queue from the TMA.
 *
 * Lifecycle: opens ONE Socket.IO connection to the gameserver with the same
 * handshake auth `useBackgammonSocket` uses (`qgammonSocketAuth()`: per-user
 * Logto `auth.accessToken`, else dev-bypass) but DOES NOT `match:join` — a socket
 * that only completes the handshake still receives the `mm:*` events, which the
 * server fans out to every one of the user's connected sockets (UserSocketIndex).
 * The per-user Logto identity is the fix for two humans sharing one userId and
 * colliding on 409 already_queued.
 *
 * `join()` POSTs `/matchmaking/queue` then waits for `mm:queued`; a 409
 * `already_queued` is treated as success (we're in the queue, just on another
 * tab/socket), while a 409 `too_many_active_matches` means the server refused
 * the search outright because we're at the concurrent-match cap — that becomes
 * a `notice`, not a silent no-op. `cancel()` DELETEs the queue. On `mm:paired`
 * we stash the payload and let the CALLER navigate to the match route. On
 * `mm:cancelled` we reset to idle and, unless WE asked for it (`reason: 'user'`),
 * raise a `notice` — notably `capped`, which the pairing worker sends when the
 * match cap filled up from another route while we were waiting.
 *
 * Pairing can take a while with few players (±50 ELO, widening to ±200 after
 * 30s), so the socket stays open for the whole wait. On unmount we cancel the
 * queue (DELETE + disconnect) for predictable UX — leaving the page does NOT keep
 * you queued.
 */
export function useRankedQueue(): RankedQueue {
  const socketRef = useRef<Socket | null>(null);
  const [status, setStatus] = useState<RankedQueueStatus>('idle');
  const [enqueuedAt, setEnqueuedAt] = useState<number | null>(null);
  const [paired, setPaired] = useState<MatchmakingPairedPayload | null>(null);
  const [notice, setNotice] = useState<RankedQueueNotice | null>(null);
  const [mode, setMode] = useState<RankedQueueMode | null>(null);

  // Latest status, read inside socket handlers without re-binding the socket.
  const statusRef = useRef(status);
  statusRef.current = status;

  // Open the listening socket once on mount. The `mm:*` events arrive on this
  // socket regardless of whether we ever `match:join`.
  useEffect(() => {
    // Handshake auth is resolved asynchronously (it may mint/refresh a Logto
    // token). `cancelled` guards an unmount that races the token resolution.
    let cancelled = false;

    void qgammonSocketAuth().then((auth) => {
      if (cancelled) return;
      const socket = io(env.qgammonWsUrl, { ...QGAMMON_SOCKET_OPTIONS, auth });
      socketRef.current = socket;

      socket.on(ServerEvents.MatchmakingQueued, (p: MatchmakingQueuedPayload) => {
        setEnqueuedAt(Date.parse(p.enqueuedAt) || Date.now());
        // Only advance to `queued` if we're still mid-join — never clobber `paired`.
        setStatus((cur) => (cur === 'queueing' || cur === 'idle' ? 'queued' : cur));
      });
      socket.on(ServerEvents.MatchmakingPaired, (p: MatchmakingPairedPayload) => {
        setPaired(p);
        setStatus('paired');
      });
      socket.on(
        ServerEvents.MatchmakingCancelled,
        (p?: Partial<MatchmakingCancelledPayload>) => {
          // Server dropped us. Only reset if we haven't already been paired — a
          // late cancel must not undo a pairing (statusRef mirrors `status`, so
          // reading it here keeps the state updater itself pure).
          const was = statusRef.current;
          if (was === 'paired') return;
          setStatus('idle');
          setEnqueuedAt(null);
          // Explain ourselves ONLY if this client was actually searching. The
          // gameserver fans `mm:*` out to every socket the user has open, so a
          // queue they started (and lost) on another device would otherwise pop
          // an unexplained sheet on this one.
          if (was !== 'queueing' && was !== 'queued') return;
          // `user` = we asked for it (Cancel, or the unmount DELETE) — no notice.
          // Anything else deserves an explanation, and a reason this bundle has
          // never heard of degrades instead of leaking a raw protocol token.
          const raw: unknown = p?.reason;
          if (raw === 'user') return;
          setNotice({
            kind: 'cancelled',
            reason: isMatchmakingCancelReason(raw) ? raw : 'unknown',
          });
        },
      );
    });

    return () => {
      cancelled = true;
      // Cancel on unmount: best-effort DELETE so we don't linger in the queue
      // after leaving the page, then drop the socket.
      if (statusRef.current === 'queued' || statusRef.current === 'queueing') {
        void deleteQueue();
      }
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
    // Mount-once: the socket lifecycle is independent of join/cancel calls.
    // Cleanup reads only stable refs and the module-level deleteQueue helper,
    // so there are no reactive dependencies to list here.
  }, []);

  const join = useCallback((mode: RankedQueueMode) => {
    setNotice(null);
    setPaired(null);
    setMode(mode);
    setStatus('queueing');
    /**
     * Rilegge l'eco e, se la coda non e' quella chiesta, ESCE: avviso, modo a
     * `null`, `DELETE`. Vero se e' uscito. Lo stesso passo per il `200` e per
     * il `409 already_queued` — due strade verso la stessa domanda.
     */
    const esciSeCodaSbagliata = (asked: RankedQueueMode, echo: QueueEcho | null): boolean => {
      const axis = queueMismatch(asked, echo);
      if (axis === null) return false;
      setStatus('idle');
      setNotice({
        kind: 'wrongQueue',
        asked,
        got: modeOfTimeControl(echo?.timeControl),
        axis,
      });
      setMode(null);
      // Si ESCE dal bacino sbagliato: lasciarci dentro il giocatore
      // significa un'attesa che non finisce — o, sulla coda libera, un ELO
      // scritto a chi aveva scelto di non rischiarlo.
      void deleteQueue();
      return true;
    };
    void (async () => {
      try {
        // Logto Bearer if signed in, else dev-bypass headers (dev only).
        const headers = await qgammonAuthHeaders(true);
        const res = await fetch(`${env.qgammonWsUrl}/matchmaking/queue`, {
          method: 'POST',
          headers,
          // ⛔ Il corpo lo decide `queueBody`, che un banco esegue: qui non si
          // costruisce un oggetto a mano. `clocked` manda `{}` e SEGUE il
          // default del server — cablare 180/10 lo inchioderebbe al valore di
          // oggi (v. `lib/qgammon/queueMode.ts`).
          body: JSON.stringify(queueBody(mode)),
        });
        if (res.ok) {
          // 🔴 SI RILEGGE CIO' CHE IL SERVER RIMANDA, sempre — stessa
          // disciplina del ritiro Limulo (#210): il rifiuto arriva muto.
          // ⚠️ Sull'OROLOGIO il confronto e' sull'ASSE (con/senza), non sui
          // numeri: «con orologio» significa «quello che dice il server», quindi
          // pretendere 180/10 andrebbe rosso il giorno che il default cambia.
          // 🔴 Su `rated` invece la coda LIBERA vuole la conferma esplicita
          // (`rated === false`): un corpo illeggibile qui vale «non confermata»
          // ⇒ `null` passa a `queueMismatch`, che per la classificata non ci
          // vede niente di sbagliato e per la libera si'.
          const body = (await res.json().catch(() => null)) as QueueEcho | null;
          if (esciSeCodaSbagliata(mode, body)) return;
          return; // wait for mm:queued to flip to `queued`
        }
        if (res.status === 409) {
          // TWO different 409s share this status, so read the body before
          // deciding. `too_many_active_matches` = the server refused the search
          // because we're at the concurrent-match cap; pretending we're queued
          // would leave the player watching a spinner that can never resolve.
          const body = (await res.json().catch(() => ({}))) as {
            error?: string;
            max?: number;
          };
          if (body.error === 'too_many_active_matches') {
            setStatus('idle');
            setNotice({ kind: 'atMatchCap', max: body.max ?? null });
            return;
          }
          // Otherwise: already in the queue from another session of the SAME
          // user (now that identity is per-user, this is a genuine duplicate —
          // no longer two different humans colliding on the shared "Dev" id).
          //
          // 🔴 E DA QUANDO LE CODE SONO DUE, «gia' in coda» PUO' VOLER DIRE
          // «gia' in coda NELL'ALTRO BACINO». [M `or43` 09/09, staging, token
          // vero: in coda con `{}`, un secondo POST con `unlimited` torna
          // `409 already_queued` e la GET continua a dire 180/10.] ⇒ chi ha la
          // TMA aperta in un'altra scheda chiede «senza orologio», legge «sei
          // in coda», e sta nel bacino con orologio. **Finche' la coda era una
          // sola un `already_queued` non poteva essere il bacino sbagliato:
          // adesso puo'.**
          // ⇒ Si CHIEDE al server in quale bacino siamo prima di dire «sei in
          // coda»: la GET porta gia' `timeControl`. Non si avanza ottimisti su
          // una domanda che ha una risposta.
          const stato = await fetchQueueStatus();
          // ⚠️ `stato === null` = la GET non ha risposto, o dice che non siamo
          // in coda: per la CLASSIFICATA non e' un disaccordo, e' un «non lo
          // so» — stessa regola del ramo 200, si prosegue con l'orologio
          // locale. Per la coda LIBERA «non lo so» non basta: senza un
          // `rated === false` letto non si resta (v. `queueMismatch`).
          if (esciSeCodaSbagliata(mode, stato)) return;
          setStatus((cur) => (cur === 'queueing' ? 'queued' : cur));
          setEnqueuedAt((cur) => cur ?? stato?.enqueuedAt ?? Date.now());
          return;
        }
        throw new Error(`queue failed: ${res.status}`);
      } catch {
        setNotice({ kind: 'failed' });
        setStatus('idle');
      }
    })();
  }, []);

  const cancel = useCallback(() => {
    setStatus('idle');
    setEnqueuedAt(null);
    setPaired(null);
    setNotice(null);
    // ⚠️ E il MODO torna a `null`: da fermi non si sta cercando in nessuno dei
    // due bacini, e lasciarlo acceso farebbe dire alla prossima schermata in
    // quale coda «sei» quando non sei in nessuna.
    setMode(null);
    void deleteQueue();
  }, []);

  // Il modo resta finche' l'avviso e' a schermo: l'avviso dice perche' la
  // ricerca si e' fermata, e in QUALE coda si era fermata fa parte del perche'.
  const dismissNotice = useCallback(() => {
    setNotice(null);
    setMode(null);
  }, []);

  return { status, enqueuedAt, paired, notice, mode, join, cancel, dismissNotice };
}

/** Fire-and-forget DELETE of the queue entry (used by cancel + unmount cleanup). */
async function deleteQueue(): Promise<void> {
  try {
    const headers = await qgammonAuthHeaders();
    await fetch(`${env.qgammonWsUrl}/matchmaking/queue`, {
      method: 'DELETE',
      headers,
    });
  } catch {
    /* best-effort */
  }
}

/**
 * Recover the server-side enqueue time on a 409 (already queued from another
 * session of the same user). `GET /matchmaking/queue` returns `{ inQueue,
 * waitingMs }`; we derive `enqueuedAt = now - waitingMs` so the elapsed clock is
 * accurate instead of restarting from "now". Returns null if not in queue or on
 * any error (the caller then falls back to Date.now()).
 */
/**
 * Lo stato della coda come lo dice il server: da quando, **e in QUALE bacino**.
 *
 * 🔴 `timeControl` non e' un extra: prima questo tipo dichiarava solo
 * `{inQueue, waitingMs}` e **buttava via** il campo che il server manda
 * (`matchmaking/routes.ts:184-191` @ `QGammon origin/main`). Finche' la coda era
 * UNA sola non serviva a niente; con due bacini e' l'unica cosa che dice in
 * quale dei due sei — e il ramo `409 already_queued` non ha altro modo di
 * saperlo. *Un campo scartato mentre era inutile diventa il campo che manca.*
 */
async function fetchQueueStatus(): Promise<{
  enqueuedAt: number | null;
  timeControl: unknown;
  /** L'eco di `rated` (pezzo Q di qgammon-orcr): assente su un server che non lo conosce. */
  rated: unknown;
} | null> {
  try {
    const headers = await qgammonAuthHeaders();
    const res = await fetch(`${env.qgammonWsUrl}/matchmaking/queue`, { headers });
    if (!res.ok) return null;
    const body = (await res.json()) as {
      inQueue?: boolean;
      waitingMs?: number;
      timeControl?: unknown;
      rated?: unknown;
    };
    if (!body.inQueue) return null;
    return {
      enqueuedAt: typeof body.waitingMs === 'number' ? Date.now() - body.waitingMs : null,
      timeControl: body.timeControl,
      rated: body.rated,
    };
  } catch {
    return null;
  }
}
